/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import { hasChildren } from 'src/core/utils/commonEntityHelpers';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { isEntityOfType } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

// @todo This file contains duplicate logic. This will be de-duplicated in the future.

/**
 * A factory function to create an empty
 * simple entity.
 */
export const createEmptySimpleEntity = (): SimpleEntity => ({
    id: getId(),
    name: '',
    typeId: ModelType.Simple,
    children: [],
    features: [],
    roles: [],
    labelsCount: 0
});

/**
 * Checks whether this entity has features or not.
 *
 * @param entity The entity to check.
 */
export const hasFeatures = (entity: SimpleEntity): boolean => entity.features !== undefined && entity.features.length !== 0;

/**
 * A function that searches for a child through the
 * children of the parent entity.
 */
export const getChildById = (entities: Entity[], parentId: LuisId, childId: LuisId): SimpleEntity => {
    const foundEntity = entities.find(e => e.id === parentId);

    if (foundEntity) {
        return getChildByIdHelper((<SimpleEntity>foundEntity).children, childId);
    }

    return undefined;
};

export const getChildByIdHelper = (children: Entity[], childId: LuisId): SimpleEntity => {
    const foundChild = children.find(child => child.id === childId);

    if (foundChild) {
        return <SimpleEntity>foundChild;
    }

    for (const child of children) {
        if (isEntityOfType<SimpleEntity>(child, ModelType.Simple)) {
            if (!child.instanceOf) {
                const found = getChildByIdHelper(child.children, childId);

                if (found) {
                    return found;
                }
            }
        }
    }

    return undefined;
};
export const getChildStoreById = (entityStores: EntityStore[], parentId: LuisId, childId: LuisId): SimpleEntityStore => {
    const foundEntity = entityStores.find(e => e.id === parentId);

    if (foundEntity) {
        return getChildStoreByIdHelper((<SimpleEntityStore>foundEntity).children, childId);
    }

    return undefined;
};

export const getChildStoreByIdHelper = (children: EntityStore[], childId: LuisId): SimpleEntityStore => {
    const foundChild = children.find(child => child.id === childId);

    if (foundChild) {
        return <SimpleEntityStore>foundChild;
    }

    for (const child of children) {
        if (child.typeId === ModelType.Simple) {
            if (!(<SimpleEntityStore>child).instanceOf && (<SimpleEntityStore>child).children.length) {
                const found = getChildStoreByIdHelper((<SimpleEntityStore>child).children, childId);

                if (found) {
                    return found;
                }
            }
        }
    }

    return undefined;
};

/**
 * Receives simple child entity and its absolute parent
 * and returns its direct parent.
 */
export const getDirectParent = (entity: SimpleEntity, absoluteParent: SimpleEntity): SimpleEntity | undefined => {
    const foundChild = absoluteParent.children.find(child => child.id === entity.id);

    if (foundChild) {
        return absoluteParent;
    }

    for (const child of absoluteParent.children) {
        const foundDirectParent = getDirectParent(entity, child);

        if (foundDirectParent) {
            return foundDirectParent;
        }
    }
};

/**
 * Gets the absolute parent of an entity.
 */
export const getAbsoluteParent = (childEntity: SimpleEntity, entities: Entity[]): SimpleEntity | undefined =>
    getAbsoluteParentById(childEntity.id, entities);

/**
 * Gets the absolute parent of an entity by its id.
 */
export const getAbsoluteParentById = (childEntityId: LuisId, entities: Entity[]): SimpleEntity | undefined => {
    const childEntity = getChildByIdHelper(entities, childEntityId);

    if (!childEntity) {
        return undefined;
    }

    for (const absoluteParent of entities) {
        if (absoluteParent.typeId === ModelType.Simple) {
            if (absoluteParent.id === childEntity.id) {
                return <SimpleEntity>absoluteParent;
            }
            const directParent = getDirectParent(childEntity, <SimpleEntity>absoluteParent);
            if (directParent) {
                return <SimpleEntity>absoluteParent;
            }
        }
    }
};

type DepthPath = { path: string[]; depth: number };

/**
 * Gets the a string path of entity names that were
 * walked to get to the given entity. Also returns the
 * depth of that entity. Primarily used in the brush mode.
 *
 * @param entity The entity to get.
 * @param parentEntity The root parent entity to start search at.
 * @param param2 The depth and path information that will be
 * passed with the recursive calls.
 */
export const getNDepthPath = (
    entity: SimpleEntity,
    parentEntity: SimpleEntity,
    { depth, path }: DepthPath = { depth: 1, path: [] }
): DepthPath | undefined => {
    if (!parentEntity.children.length) {
        return undefined;
    }

    if (parentEntity.children.find(child => child.id === entity.id)) {
        return { depth: depth + 1, path: [...path, parentEntity.name, entity.name] };
    }

    return parentEntity.children
        .map(child => getNDepthPath(entity, child, { depth: depth + 1, path: [...path, parentEntity.name] }))
        .filter(Boolean)[0];
};

/**
 * Gets the child entity to be updated by iterating recursively on the children entity tree.
 *
 * @param entityId The entity id of the parent entity where the new child to be added.
 * @param entity The root parent to search in its children .
 */
export const getChildEntity = (entityId: React.ReactText, entity: SimpleEntity): SimpleEntity => {
    if (entity && entity.id === entityId) {
        return entity;
    }

    if (!hasChildren(entity)) {
        return null;
    }

    for (const child of entity.children) {
        const found = getChildEntity(entityId, child);

        if (found) {
            return found;
        }
    }

    return null;
};

/**
 * Flatten the nested parent children arrays
 * e.g. :
 * [{name: x, children : [{name: y, children: [{name: z}]}]}, {name: w}]
 * -> [{name: x}, {name: y}, {name: z}, {name: w}]
 */
export const flattenNDepthChildren = (parents: SimpleEntity[], buffer: SimpleEntity[] = []): SimpleEntity[] => {
    parents.forEach(parent => {
        buffer.push(parent);
        if (parent.children !== undefined) {
            flattenNDepthChildren(parent.children, buffer);
        }
    });

    return buffer;
};
